import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {dataToolSchemas} from '../../utils/schemas';

const toolName = 'list-pages' as const;

const argumentZod = z.object({
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .describe('Maximum number of pages to return (default: 100, max: 100)'),
  offset: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe('Number of pages to skip (default: 0)'),
  localeId: dataToolSchemas.localeId.optional(),
});

export const listPagesManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'List pages',
  family: 'pages',
  llmTool: {
    name: toolName,
    description:
      'Lists all pages for the current site. Returns page metadata including title, slug, SEO settings, and Open Graph data, but excludes page content. Supports pagination and locale-specific data.',
    inputSchema: argumentZod,
  },
  type: 'server',
  // Site-scoped check only. `SitePageSettings:read` is restriction-gated
  // per-page in policy, but this tool accepts no pageId, so we cannot
  // authorize per page here. To avoid leaking settings for pages a
  // restricted user shouldn't see, the server handler strips `seo` and
  // `openGraph` from each row — see `entrypoints/server/lib/logic/ai/tools/list-pages.ts`.
  // The returned shape is therefore a page-directory subset (id, title,
  // slug, etc.), not full page settings.
  permission: [{resource: 'SitePageSettings', action: 'read'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
});

export type ListPagesArgumentType = z.infer<typeof argumentZod>;
